import commonServices from 'ApiService/Common/commonServices';
import branchService from 'ApiService/SuperAdmin/branchService';
import headhrStudentService from 'ApiService/HeadHR/headhrStudentService';
import { getNotificationNavigationTarget } from 'components/navbar/notficationredirection';
import { AuthContext } from 'contexts/AuthContext';
import { formatDateText } from 'pages/helpers/dateUtils';
import { useContext, useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import ConfirmationModal from '../ConfirmationModal';

const Index = () => {
  const [notifications, setNotifications] = useState([]);
  const { state } = useContext(AuthContext);
  const isHeadHR =
    state?.user?.role === 'placement' && state?.user?.hrRole === 'headhr';
  // Super admins and head HRs can approve/reject unpause requests
  const isPendingUnpauseForApprover = (notification) =>
    (state?.user?.role === 'superadmin' || isHeadHR) &&
    notification.type === 'Unpause Placement Request' &&
    (!notification.verificationStatus ||
      notification.verificationStatus === 'pending');
  const [loading, setLoading] = useState(false);
  const [processingIds, setProcessingIds] = useState([]);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredNotifications, setFilteredNotifications] = useState([]);

  // Confirmation modal states
  const [isUnpauseModalOpen, setIsUnpauseModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState({
    notificationId: '',
    studentId: '',
    isApproved: false,
  });

  const notificationTypes = useMemo(() => {
    const types = [...new Set(notifications.map((note) => note.type))];
    return types;
  }, [notifications]);

  useEffect(() => {
    if (state?.user?.role && state?.user?.role !== 'superadmin') {
      fetchJobs(state?.user?.role);
    }
  }, [state?.user?.role]);

  useEffect(() => {
    let filtered = notifications;

    if (selectedTopic) {
      filtered = filtered.filter(
        (notification) => notification.type === selectedTopic
      );
    }

    if (searchQuery) {
      filtered = filtered.filter((notification) => {
        const studentName = notification.studentData?.studentName || '';
        const message = notification.message || '';
        const otherFields = [
          notification.studentData?.studentEmail || '',
          notification.studentData?.unpauseReason || '',
        ].join(' ');

        const searchTerm = searchQuery.toLowerCase();

        return (
          studentName.toLowerCase().includes(searchTerm) ||
          message.toLowerCase().includes(searchTerm) ||
          otherFields.toLowerCase().includes(searchTerm)
        );
      });
    }

    setFilteredNotifications(filtered);
    setCurrentPage(1);
  }, [notifications, selectedTopic, searchQuery]);

  const fetchJobs = async (role) => {
    try {
      setLoading(true);
      const response = await commonServices.getJobNotifications(role);
      if (response.data.success) {
        setNotifications(
          response.data.notifications.map((notification) => ({
            ...notification,
            studentData: notification.data,
          }))
        );
      } else {
        toast.error('No notifications found.');
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch job notifications.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const socketURL = `${process.env.REACT_APP_API_BASE_URL.replace(
      '/api/v1',
      ''
    )}`;

    const socket = io(socketURL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      console.log('Connected to socket server');
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    socket.on('NotifyUser', (data) => {
      console.log('event:', data);
      if (data.recipient === state?.user?.id) {
        const newNotification = {
          _id: data.notificationId,
          type: data.type,
          message: data.message,
          studentData: data.studentData,
          isRead: false,
          createdAt: new Date().toISOString(),
          verificationStatus: data.verificationStatus,
        };
        setNotifications((prev) => [newNotification, ...prev]);
        toast.success('New notification received!');
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [state?.user]);

  const handleNotificationClick = async (notification) => {
    if (isPendingUnpauseForApprover(notification)) {
      return;
    }

    try {
      if (!notification.isRead) {
        await commonServices.markNotificationAsRead(
          state?.user?.role,
          notification._id
        );

        setNotifications((prev) =>
          prev.map((item) =>
            item._id === notification._id ? { ...item, isRead: true } : item
          )
        );
      }

      const targetPath = getNotificationNavigationTarget(
        notification.type,
        state?.user?.role,
        notification.data || notification.studentData,
        state?.user?.hrRole
      );

      navigate(targetPath);
    } catch (error) {
      console.error('Error handling notification click:', error);
      toast.error('Failed to process notification. Please try again.');
    }
  };

  // Open unpause placement confirmation modal
  const openUnpauseModal = (notificationId, studentId, isApproved) => {
    setModalAction({ notificationId, studentId, isApproved });
    setIsUnpauseModalOpen(true);
  };

  const handleUnpausePlacement = async () => {
    const { notificationId, studentId, isApproved } = modalAction;

    try {
      setProcessingIds((prev) => [...prev, notificationId]);
      setIsUnpauseModalOpen(false);

      const approveService = isHeadHR
        ? headhrStudentService.approveUnpausePlacement
        : branchService.approveUnpausePlacement;
      const response = await approveService(
        notificationId,
        isApproved,
        isApproved ? 'Approved unpause request' : 'Denied unpause request'
      );

      if (response.data.success) {
        setNotifications((prev) =>
          prev.map((notification) =>
            notification._id === notificationId
              ? {
                  ...notification,
                  isRead: true,
                  verificationStatus: isApproved ? 'approved' : 'rejected',
                  actionBy: {
                    name: state?.user?.name,
                    role: isHeadHR ? 'Head HR' : 'Super Admin',
                  },
                  actionAt: new Date().toISOString(),
                }
              : notification
          )
        );

        toast.success(
          `Placement ${isApproved ? 'resumed' : 'paused'} successfully`
        );
      } else {
        toast.error(
          response.data.message || 'Failed to process unpause request'
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to process unpause request'
      );
    } finally {
      setProcessingIds((prev) => prev.filter((id) => id !== notificationId));
    }
  };

  const getStatusColor = (verificationStatus) => {
    switch (verificationStatus) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'denied':
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderUnpausePlacementDetails = (studentData) => {
    if (!studentData) return null;

    return (
      <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
        <p className="flex items-center">
          <span className="min-w-24 font-medium">Student Name:</span>
          <span className="ml-2">{studentData.studentName}</span>
        </p>
        <p className="flex items-center">
          <span className="min-w-24 font-medium">Email:</span>
          <span className="ml-2">{studentData.studentEmail}</span>
        </p>
        <p className="flex items-center">
          <span className="min-w-24 font-medium">Unpause Reason:</span>
          <span className="ml-2">{studentData.unpauseReason || 'N/A'}</span>
        </p>
        {studentData.class && (
          <>
            <p className="flex items-center">
              <span className="min-w-24 font-medium">Course:</span>
              <span className="ml-2">
                {studentData.class.courseName || 'N/A'}
              </span>
            </p>
            <p className="flex items-center">
              <span className="min-w-24 font-medium">Batch Number:</span>
              <span className="ml-2">
                {studentData.class.batchNumber || 'N/A'}
              </span>
            </p>
          </>
        )}
      </div>
    );
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredNotifications.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <BallTriangle color="#5431ed" height={50} width={50} />
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-gray-500">
        No notifications found.
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Unpause Placement Confirmation Modal */}
      <ConfirmationModal
        isOpen={isUnpauseModalOpen}
        onClose={() => setIsUnpauseModalOpen(false)}
        onConfirm={handleUnpausePlacement}
        title="Confirm Placement Status Change"
        message={`Are you sure you want to ${
          modalAction.isApproved ? 'resume' : 'deny resuming'
        } this student's placement?`}
        confirmText={
          processingIds.includes(modalAction.notificationId)
            ? 'Processing...'
            : modalAction.isApproved
            ? 'Resume Placement'
            : 'Deny'
        }
        cancelText="Cancel"
        icon="info"
        disableConfirm={processingIds.includes(modalAction.notificationId)}
      />

      {/* Filter Controls */}
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <h1 className="text-xl font-bold text-gray-800 dark:text-white">
          Notifications
        </h1>

        <div className="flex flex-col space-y-4 md:flex-row md:items-center md:space-x-4 md:space-y-0">
          {/* Filter by Topic Type */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Type:
            </span>
            <select
              value={selectedTopic}
              onChange={(e) => setSelectedTopic(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
            >
              <option value="">All Types</option>
              {notificationTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Search by Student Name */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Search:
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Student name..."
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* No Results */}
      {currentItems.length === 0 ? (
        <div className="flex h-40 items-center justify-center">
          <div className="text-center text-gray-500">
            <p className="text-lg font-medium">No notifications found</p>
            <p className="mt-1 text-sm">
              {selectedTopic || searchQuery
                ? 'No notifications match your current filters'
                : 'New notifications will appear here'}
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-3 space-y-4 md:mt-3">
          {currentItems.map((notification) => (
            <div
              key={notification._id || Date.now()}
              onClick={
                isPendingUnpauseForApprover(notification)
                  ? undefined
                  : () => handleNotificationClick(notification)
              }
              className={`overflow-hidden rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-all duration-200 dark:border-gray-700 dark:!bg-navy-800 ${
                isPendingUnpauseForApprover(notification)
                  ? ''
                  : 'cursor-pointer hover:shadow-md'
              }`}
            >
              <div className="flex justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    {notification.type}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {notification.message}
                  </p>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDateText
                    ? formatDateText(notification.createdAt || Date.now())
                    : new Date(
                        notification.createdAt || Date.now()
                      ).toLocaleDateString()}
                </span>
              </div>

              {notification.type === 'Unpause Placement Request' &&
                renderUnpausePlacementDetails(notification.studentData)}

              <div className="mt-4 flex flex-col items-start justify-between space-y-4 pt-2 sm:flex-row sm:items-center sm:space-y-0">
                {notification?.verificationStatus &&
                  notification.verificationStatus !== null && (
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${getStatusColor(
                        notification.verificationStatus
                      )}`}
                    >
                      {notification.verificationStatus
                        ? notification.verificationStatus
                            .charAt(0)
                            .toUpperCase() +
                          notification.verificationStatus.slice(1)
                        : 'Pending'}
                    </span>
                  )}

                {notification.actionBy?.name && (
                  <span className="text-xs text-gray-600 dark:text-gray-300">
                    {notification.verificationStatus === 'approved'
                      ? 'Approved'
                      : 'Rejected'}{' '}
                    by{' '}
                    <span className="font-semibold">
                      {notification.actionBy.name}
                    </span>{' '}
                    ({notification.actionBy.role})
                    {notification.actionAt &&
                      ` · ${new Date(notification.actionAt).toLocaleString()}`}
                  </span>
                )}

                {/* Unpause Placement Request Buttons */}
                {isPendingUnpauseForApprover(notification) &&
                  notification.studentData && (
                    <div className="flex gap-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openUnpauseModal(
                            notification._id,
                            notification.studentData.studentId,
                            true
                          );
                        }}
                        disabled={processingIds.includes(notification._id)}
                        className="inline-flex items-center rounded-md bg-green-500 px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {processingIds.includes(notification._id) ? (
                          <>Processing...</>
                        ) : (
                          'Resume Placement'
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openUnpauseModal(
                            notification._id,
                            notification.studentData.studentId,
                            false
                          );
                        }}
                        disabled={processingIds.includes(notification._id)}
                        className="inline-flex items-center rounded-md bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {processingIds.includes(notification._id) ? (
                          <>Processing...</>
                        ) : (
                          'Deny'
                        )}
                      </button>
                    </div>
                  )}
              </div>

              <div className="mt-2 flex items-center text-xs text-gray-500 dark:text-gray-400">
                <span>Type: {notification.type}</span>
                <span className="ml-4">
                  Status: {notification.isRead ? 'Read' : 'Unread'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {filteredNotifications.length > 0 && (
        <div className="mt-6 flex items-center justify-between border-t border-gray-200 px-4 py-3 dark:border-gray-700">
          <div className="flex items-center">
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1); // Reset to first page when changing items per page
              }}
              className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
            >
              {/* <option value={5}>5 per page</option> */}
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
            </select>
            <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
              Showing {indexOfFirstItem + 1} to{' '}
              {Math.min(indexOfLastItem, filteredNotifications.length)} of{' '}
              {filteredNotifications.length} results
            </span>
          </div>

          <div className="flex space-x-1">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
            >
              Previous
            </button>

            {/* Page number buttons - show up to 5 pages with ellipsis for compact UI */}
            {Array.from({ length: Math.min(totalPages, 5) }).map((_, idx) => {
              let pageNum;

              if (totalPages <= 5) {
                // If we have 5 or fewer pages, show all page numbers
                pageNum = idx + 1;
              } else if (currentPage <= 3) {
                // If we're near the start, show pages 1-5
                pageNum = idx + 1;
              } else if (currentPage >= totalPages - 2) {
                // If we're near the end, show the last 5 pages
                pageNum = totalPages - 4 + idx;
              } else {
                // Otherwise, show 2 pages before current, current, and 2 pages after
                pageNum = currentPage - 2 + idx;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`inline-flex items-center rounded-md border px-3 py-1 text-sm font-medium shadow-sm ${
                    currentPage === pageNum
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-600 dark:bg-indigo-900 dark:text-indigo-300'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Index;
